import React from "react";
import { Link } from "react-router-dom";
import { Leaf, Heart, Globe, Sparkles } from "lucide-react";
import { ABOUT_IMAGE, HERO_IMAGE, BEARD_IMAGE } from "../mock";

const pillars = [
  { icon: Leaf, title: "Purity", text: "Unrefined, small-batch formulas with nothing to hide." },
  { icon: Globe, title: "Provenance", text: "Ingredients sourced directly and ethically from Chad." },
  { icon: Heart, title: "Community", text: "Fair partnerships that honour the women behind the craft." },
  { icon: Sparkles, title: "Results", text: "Time-tested rituals proven across generations." }
];

const About = () => {
  return (
    <div>
      {/* Hero */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b0a09]/85 via-[#0b0a09]/75 to-[#0b0a09]" />
        </div>
        <div className="relative max-w-[820px] mx-auto px-6 text-center">
          <p className="text-[#c9a24b] text-sm tracking-[0.3em] uppercase mb-6">Our Story</p>
          <h1 className="font-display text-[46px] md:text-[68px] text-[#f4efe4] leading-tight">
            Rooted in Tradition, <span className="italic text-gold-gradient">Powered by Nature</span>
          </h1>
          <p className="text-[#c8c0b2] text-lg mt-7 leading-relaxed">
            Kanama Naturals was born from a simple belief — that the purest beauty
            rituals are the ones passed down through time.
          </p>
        </div>
      </section>

      {/* Story split */}
      <section className="bg-[#141210] border-y border-[#2c2822]">
        <div className="max-w-[1280px] mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#c9a24b] text-sm tracking-[0.3em] uppercase mb-5">The Beginning</p>
            <h2 className="font-display text-[36px] md:text-[48px] text-[#f4efe4] leading-tight mb-7">
              From the heart of Chad to your ritual
            </h2>
            <p className="text-[#c8c0b2] leading-relaxed mb-5">
              Our founder grew up watching the women of her community blend Chebe,
              shea and fragrant grasses into treatments that kept their hair strong,
              long and radiant. These weren't products — they were rituals of care
              and connection.
            </p>
            <p className="text-[#a29a8c] leading-relaxed">
              Kanama Naturals exists to share that heritage with the world, while
              honouring the communities who created it. We keep our formulas
              unrefined and our sourcing ethical, so every jar carries the truth of
              its origin.
            </p>
          </div>
          <img src={ABOUT_IMAGE} alt="Chebe ritual" className="w-full aspect-[4/5] object-cover rounded-lg" />
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-[1280px] mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <p className="text-[#c9a24b] text-sm tracking-[0.3em] uppercase mb-4">What We Stand For</p>
          <h2 className="font-display text-[38px] md:text-[50px] text-[#f4efe4]">Our Four Pillars</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((p, i) => (
            <div key={i} className="bg-[#141210] border border-[#2c2822] rounded-lg p-8 card-hover">
              <div className="w-14 h-14 rounded-full border border-[#c9a24b]/40 flex items-center justify-center mb-6">
                <p.icon size={24} className="text-[#c9a24b]" strokeWidth={1.4} />
              </div>
              <h3 className="font-display text-2xl text-[#f4efe4] mb-3">{p.title}</h3>
              <p className="text-[#a29a8c] text-sm leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Quote band */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={BEARD_IMAGE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0b0a09]/85" />
        </div>
        <div className="relative max-w-[760px] mx-auto px-6 text-center">
          <p className="font-display text-[32px] md:text-[44px] text-[#f4efe4] italic leading-snug">
            “We don't invent beauty — we protect the wisdom that has always known it.”
          </p>
          <p className="text-[#c9a24b] text-sm tracking-[0.25em] uppercase mt-8">— The Kanama Family</p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1280px] mx-auto px-6 py-24 text-center">
        <h2 className="font-display text-[38px] md:text-[52px] text-[#f4efe4] mb-8">
          Experience the ritual for yourself
        </h2>
        <Link to="/shop" className="btn-gold inline-flex px-10 py-4 rounded-full text-sm tracking-[0.18em] uppercase font-medium">
          Shop the Collection
        </Link>
      </section>
    </div>
  );
};

export default About;
